import axios, { type AxiosInstance, type AxiosError } from "axios";
import { toastManager } from "@/lib/toast-manager";
import { HttpMethod, HttpStatusCode } from "@/types/https";

export interface ApiCallParams {
  url: string;
  method: HttpMethod;
  params?: Record<string, any>;
  body?: Record<string, any>;
  headers?: Record<string, string>;
  withCredentials?: boolean;
  /** Skip showing error toast for this request */
  silent?: boolean;
}

export interface ApiClientConfig {
  baseURL: string;
  getToken?: () => string | null | Promise<string | null>;
  onError?: (error: AxiosError) => void;
}

const getDefaultToken = async (): Promise<string | null> => {
  const { getAccessToken } = await import("./workchestra/handler/auth");
  return getAccessToken();
};

export interface ApiErrorResponse {
  message?: string;
  title?: string;
  errors?: Record<string, string[]>;
  status?: number;
}

export const paramsSerializer = (params: Record<string, any>): string => {
  if (!params || Object.keys(params).length === 0) {
    return "";
  }

  return Object.entries(params)
    .flatMap(([key, value]) => {
      if (value === null || value === undefined) {
        return [];
      }

      if (Array.isArray(value)) {
        return value.map(
          (val) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(String(val))}`
        );
      }

      if (key === "encodeQuery" && typeof value === "object") {
        return Object.entries(value)
          .filter(([, v]) => Array.isArray(v))
          .map(([k, v]) => `${k}=${encodeURIComponent(JSON.stringify(v))}`);
      }

      // Handle regular values
      return `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
    })
    .filter(Boolean)
    .join("&");
};

export const extractErrorMessage = (
  error: AxiosError<ApiErrorResponse>
): string => {
  const data = error.response?.data;

  if (typeof data === "string") {
    return data;
  }

  return (
    data?.title ||
    data?.message ||
    error.message ||
    "An unexpected error occurred"
  );
};

export const handleApiError = (
  error: AxiosError<ApiErrorResponse>,
  options?: { silent?: boolean; method?: HttpMethod }
): void => {
  if (options?.silent) return;

  const status = error.response?.status;
  const method = options?.method;

  // Skip toast for 404 on GET requests (common for "not found" scenarios)
  if (method === HttpMethod.GET && status === HttpStatusCode.NOT_FOUND) {
    return;
  }

  const message = extractErrorMessage(error);

  switch (status) {
    case HttpStatusCode.UNAUTHORIZED:
      toastManager.showError("Unauthorized", "Please login again");
      break;
    case HttpStatusCode.FORBIDDEN:
      toastManager.showError("Forbidden", "You don't have permission");
      break;
    case HttpStatusCode.NOT_FOUND:
      toastManager.showError("Not Found", message);
      break;
    case HttpStatusCode.INTERNAL_SERVER_ERROR:
      toastManager.showError("Server Error", "Please try again later");
      break;
    default:
      if (method === HttpMethod.GET) {
        toastManager.showError("Failed to fetch data", message);
      } else {
        toastManager.showError("Request Failed", message);
      }
  }
};

export const createApiClient = (config: ApiClientConfig) => {
  const instance: AxiosInstance = axios.create({
    baseURL: config.baseURL,
    paramsSerializer,
  });

  const call = <T>(params: ApiCallParams): Promise<T> => {
    const {
      url,
      method,
      params: queryParams,
      body,
      headers,
      withCredentials,
      silent,
    } = params;

    const tokenPromise = Promise.resolve(
      config.getToken?.() ?? getDefaultToken()
    );

    return tokenPromise
      .then((token) => {
        const requestHeaders: Record<string, string> = {
          ...headers,
        };

        if (
          [
            HttpMethod.POST,
            HttpMethod.PUT,
            HttpMethod.DELETE,
            HttpMethod.PATCH,
          ].includes(method)
        ) {
          requestHeaders["Content-Type"] = "application/json";
        }

        if (token) {
          requestHeaders["Authorization"] = `Bearer ${token}`;
        }

        return instance.request<T>({
          url,
          method,
          params: queryParams,
          data: body,
          headers: requestHeaders,
          withCredentials,
        });
      })
      .then((response) => response.data)
      .catch((error: AxiosError<ApiErrorResponse>) => {
        handleApiError(error, { silent, method });

        config.onError?.(error);

        throw error.response?.data || error;
      });
  };

  return {
    instance,
    call,
  };
};

export { HttpMethod, HttpStatusCode };
